import { FC, useState } from 'react';
import { EditableDataTable, EditableColumn, Select, Button, Modal, ModalFooter, Input } from '@/components/ui';
import { SxmLead, LeadStatus, LeadAutomationStatus } from '../types';
import { getLeadStatusColor, getLeadStatusLabel } from '../utils/statusHelpers';
import { useCanEdit } from '@/hooks/useEditMode';
import { CreateSxmLeadDto } from '@/hooks/useSxmLeads';
import { Plus } from 'lucide-react';

interface SxmLeadsTabProps {
  leads: SxmLead[];
  loading?: boolean;
  error?: string | null;
  userId?: string;
  updateLead: (id: string, dto: any) => Promise<SxmLead>;
  bulkUpdateLeads: (updates: Array<{ id: string; data: any }>) => Promise<SxmLead[]>;
  deleteLead: (id: string) => Promise<void>;
  createLead: (dto: CreateSxmLeadDto) => Promise<SxmLead>;
}

export const SxmLeadsTab: FC<SxmLeadsTabProps> = ({
  leads,
  loading,
  error,
  userId,
  updateLead,
  bulkUpdateLeads,
  deleteLead,
  createLead
}) => {
  // Check if editing is enabled from centralized store
  const canEdit = useCanEdit();

  // Modal state for adding new leads
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newLead, setNewLead] = useState<Partial<SxmLead>>({
    email: '',
    status: LeadAutomationStatus.NEW,
  });

  // Form validation state
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const columns: EditableColumn<SxmLead>[] = [
    {
      key: 'email',
      header: 'Email',
      editable: true,
      validate: (value: string) => {
        if (!value?.trim()) return 'Email is required';
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Invalid email format';
        }
        return undefined;
      },
    },
    {
      key: 'platform',
      header: 'Platform',
      editable: false,
    },
    {
      key: 'status',
      header: 'Status',
      editable: true,
      render: (lead: SxmLead) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLeadStatusColor(lead.status)}`}>
          {getLeadStatusLabel(lead.status)}
        </span>
      ),
      EditComponent: ({ value, onChange }) => {
        const options = Object.values(LeadStatus).map(status => ({
          value: status,
          label: getLeadStatusLabel(status),
        }));

        return (
          <Select
            value={value}
            onChange={(e) => onChange(e.target.value as LeadStatus)}
            options={options}
            fullWidth
          />
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Created',
      editable: false,
      render: (lead: SxmLead) => new Date(lead.createdAt).toLocaleDateString(),
    },
  ];


  const handleRowUpdate = async (oldRow: SxmLead, newRow: SxmLead) => {
    try {
      // Call API to update the lead
      await updateLead(oldRow.id, newRow);
    } catch (error) {
      console.error('Failed to update lead:', error);
      throw error; // Let the table handle the error
    }
  };

  const handleRowDelete = async (row: SxmLead) => {
    try {
      // Call API to delete the lead
      await deleteLead(row.id);
    } catch (error) {
      console.error('Failed to delete lead:', error);
      throw error; // Let the table handle the error
    }
  };

  // Validation function
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Email validation
    if (!newLead.email?.trim()) {
      errors.email = 'Email is required';
    } else if (newLead.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newLead.email)) {
      errors.email = 'Invalid email format';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenAddModal = () => {
    setNewLead({
      email: '',
      status: LeadAutomationStatus.NEW,
    });
    setFormErrors({});
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setNewLead({
      email: '',
      status: LeadAutomationStatus.NEW,
    });
    setFormErrors({});
  };

  const handleInputChange = (field: keyof SxmLead, value: string) => {
    setNewLead(prev => ({ ...prev, [field]: value }));

    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSaveNewLead = async () => {
    if (!userId) return;

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      const leadDto: CreateSxmLeadDto = {
        email: newLead.email?.trim() || '',
        platform: 'manual',
      };

      // Create the lead - the hook will handle updating its internal state
      await createLead(leadDto);

      handleCloseAddModal();
    } catch (error) {
      console.error('Failed to create lead:', error);
      setFormErrors({ submit: 'Failed to create lead. Please try again.' });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            SXM Leads Management
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage sales leads and their status
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={handleOpenAddModal}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Lead
          </Button>
        )}
      </div>

      {/* Show error banner within the UI instead of replacing it */}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="ml-2 flex-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-red-800">
                  {error}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="ml-3 text-xs text-red-600 underline hover:text-red-800"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show loading state within the table area */}
      {loading ? (
        <div className="flex items-center justify-center py-8 border border-gray-200 rounded-md bg-gray-50">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-sm text-gray-600">Loading...</span>
          </div>
        </div>
      ) : (
        <EditableDataTable
          columns={columns}
          data={leads}
          keyExtractor={(lead) => lead.id.toString()}
          enableEditing={canEdit}
          enableDelete={false}
          initialEditingMode="cell"
          onRowUpdate={handleRowUpdate}
          onBulkUpdate={bulkUpdateLeads}
          onRowDelete={handleRowDelete}
          tableName="sxm_leads"
        />
      )}

      {/* Add Lead Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        title="Add New Lead"
        size="lg"
      >
        <div className="space-y-6">
          {/* Submit Error */}
          {formErrors.submit && (
            <div className="rounded-md bg-red-50 p-4 border border-red-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{formErrors.submit}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <div>
                <Input
                  type="email"
                  value={newLead.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter email address"
                  className={`w-full ${formErrors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                />
                {formErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <ModalFooter>
          <Button
            onClick={handleCloseAddModal}
            variant="secondary"
            className="px-6 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewLead}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Save Lead
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};